// SOC 2 Trust Services Category breakdown, as carried by the readiness report JSON
// (`trustServicesCategories`, SOC 2 only). Entries are
// { category, total, assessed, notAssessed, scored, scopeState, readinessPercent, passing, partial, failing } — no name.
// `assessed` / `notAssessed` count criteria WITH and WITHOUT evidence; they say nothing about a
// score — a criterion answered No is evidenced and earns nothing, which is why `passing` /
// `partial` / `failing` (scored categories only, zero otherwise) are printed beside the count. Since Soc2CategoryScope, ANY category can be scored: the Common Criteria always are, and
// an optional category joins them once its scope question declares it in scope. A category that is
// excluded or undeclared is reported, not scored.
// `readinessPercent` is `double?` server-side and always present (as null when the category is not
// scored), so read it with a type check — `0` is a real score.

export const SOC2_FRAMEWORK = 'SOC 2';
export const COMMON_CRITERIA_CATEGORY = 'Common Criteria'; // Soc2TscCatalog.CommonCriteria
export const SCOPE_EXCLUDED = 'excluded';
export const SCOPE_UNDECLARED = 'undeclared';
export const SCOPE_IN_SCOPE = 'in-scope';
export const EXCLUDED_WORDING = 'Excluded from SOC 2 scope';

export const isSoc2Framework = (framework) => framework === SOC2_FRAMEWORK;

// Mirrors backend EnterpriseAssessmentsController.TrustServicesScopeWording. All four optional
// categories carry a scope declaration now (Availability, Confidentiality, Processing Integrity
// and Privacy), so any of them can read "Excluded from SOC 2 scope". The Common Criteria have no
// scope question and are always in scope; excluding them is a defensive guard only.
export const isExcludedCategory = (c) =>
    c?.category !== COMMON_CRITERIA_CATEGORY && c?.scopeState === SCOPE_EXCLUDED;

export const isUndeclaredCategory = (c) => c?.scopeState === SCOPE_UNDECLARED;

// A statement about scope, and nothing else. It read 'Assessed' / 'Not assessed' once, then
// 'Evidence present' — both described evidence, which the two count columns already carry, and
// neither described scope. It now depends only on the declaration.
export const scopeWording = (c) => {
    if (isExcludedCategory(c)) return EXCLUDED_WORDING;
    if (c?.scopeState === SCOPE_IN_SCOPE) return 'In scope';
    return 'Scope not declared';
};

export const scoredWording = (c) => (c?.scored ? 'Scored' : 'Reported, not scored');

// Mirrors backend TrustServicesPassingWording, as a phrase for the modal cell and the CSV:
// "0 passing, 1 partial" beside "2 / 2" is what stops the evidenced count reading as two passes.
// Empty for a reported-only category, which has no statuses.
export const passingWording = (c) => {
    if (!c?.scored) return '';
    // A report from a backend that predates the split has no `passing` field; say nothing rather
    // than "0 passing", which would state a count the report never made.
    if (typeof c.passing !== 'number') return '';
    const passing = `${c.passing} passing`;
    return c.partial > 0 ? `${passing}, ${c.partial} partial` : passing;
};

// Mirrors backend TrustServicesReadinessWording, in sentence case for a table cell.
export const readinessWording = (c) => {
    if (!c?.scored) return 'Not scored';
    return typeof c.readinessPercent === 'number' && Number.isFinite(c.readinessPercent)
        ? `${Math.round(c.readinessPercent)}%`
        : 'n/a';
};

// The categories nobody has declared, in the order the report lists them.
export const undeclaredCategoryNames = (categories) => {
    if (!Array.isArray(categories)) return [];
    return categories.filter(isUndeclaredCategory).map(c => c?.category);
};

// The frontend mirror of the backend's TrustServicesUnscoredNote: how to get an undeclared
// category scored. Null when every category has been declared — there is then nothing to answer.
// The Common Criteria are never named: they have no scope question, so no answer can change them.
export const scopeHintLine = (categories) => {
    const names = undeclaredCategoryNames(categories).filter(n => n !== COMMON_CRITERIA_CATEGORY);
    if (names.length === 0) return null;
    return `To score ${names.join(', ')}, answer the scope questions in the SOC 2 Optional Categories section of the assessment.`;
};

const defaultEscape = (field) => {
    const str = String(field ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

// One CSV row per category:
// Trust Services Category,<category>,<evidenced>,<total>,<Scored | Reported, not scored>,<scope wording>,<readiness>,<passing wording>
// Readiness sits after scope here and in the popup table, while the PDF table is
// "… | Scored | Readiness | Scope" — the difference is deliberate, not a bug to fix. The passing
// wording is last and empty for a reported-only category.
// Returns [] when the report carries no category breakdown (every non-SOC-2 framework).
export const buildTrustServicesCategoryCsvRows = (categories, escape = defaultEscape) => {
    if (!Array.isArray(categories)) return [];
    return categories.map(c => [
        'Trust Services Category',
        escape(c.category),
        escape(c.assessed ?? 0),
        escape(c.total ?? 0),
        escape(scoredWording(c)),
        escape(scopeWording(c)),
        escape(readinessWording(c)),
        escape(passingWording(c)),
    ].join(','));
};
